import React from 'react';
import { Col, message, Row, Table } from 'antd';
import { DotsIcon } from '@/assets/icons/icons';
import { DownloadInventoryRecord } from '@/apis/inventory';
import { useToken } from '@/common/helper';
import { CommonDrawer, CustomPagination } from '../../../components';
import { Dropdown } from '@/components/AntdCompatDropdown';
import { useUnAuthorize } from '@/hooks';
import { saveBlobAsFile } from '@/pages/Shipments/components/fileDownload';
import { CustomDeleteModal } from '@/pages/Modals';
import type {
  InventorySortDirection,
  InventorySortKey,
  InventoryUnifiedResponse,
  InventoryViewMode,
} from '../type';
import InventoryDetailDrawer from './InventoryDetailDrawer';
import { InventoryDeviceHistoryList } from './InventoryDeviceHistory';
import useInventoryTable, { FlatRow } from '../hooks/useInventoryTable';
import useInventoryColumns from '../hooks/useInventoryColumns';

type InventoryTableProps = {
  onLoadMoreGroups?: (nextPage: number) => void;
  onLoadMoreSubgroups?: (groupId: string, nextPage: number) => void;
  response?: InventoryUnifiedResponse;
  inventoryView?: InventoryViewMode;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages?: number;
  isLoading?: boolean;
  handleEditInventory?: (
    isOpen?: boolean,
    name?: string,
    id?: string,
    isDuplicate?: boolean
  ) => void;
  showDetailDrawer?: boolean;
  setShowDetailDrawer?: React.Dispatch<React.SetStateAction<boolean>>;
  deepLinkedInventoryId?: string | null;
  onDeepLinkedInventoryLoaded?: () => void;
  onCloseDetailDrawer?: () => void;
  onDeleteSuccess?: () => Promise<void> | void;
  onLoadMoreGroupItems?: (groupId: string, nextPage: number) => void;
  onLoadMoreSubgroupItems?: (
    groupId: string,
    subgroupId: string,
    nextPage: number
  ) => void;
  onSortChange?: (
    key: InventorySortKey,
    direction: InventorySortDirection
  ) => void;
  sortDirection?: InventorySortDirection;
  sortKey?: InventorySortKey;
};

const INVENTORY_TABLE_COLUMN_COUNT = 8;
const INVENTORY_DEVICE_TABLE_COLUMN_COUNT = 10;
const INVENTORY_MOBILE_SCROLL_WIDTH = 1320;
const INVENTORY_DEVICE_MOBILE_SCROLL_WIDTH = 1440;
const InventoryTable: React.FC<InventoryTableProps> = ({
  response,
  inventoryView = 'per_sku',
  page,
  setPage,
  isLoading = false,
  onLoadMoreGroups,
  onLoadMoreSubgroups,
  handleEditInventory,
  showDetailDrawer,
  setShowDetailDrawer,
  deepLinkedInventoryId,
  onDeepLinkedInventoryLoaded,
  onCloseDetailDrawer,
  onDeleteSuccess,
  onLoadMoreGroupItems,

  onLoadMoreSubgroupItems,
  onSortChange,
  sortDirection,
  sortKey,
}) => {
  const { token } = useToken();
  const { unauthorize } = useUnAuthorize();
  const [showDeviceHistory, setShowDeviceHistory] = React.useState(false);
  const [drawerMenuOpen, setDrawerMenuOpen] = React.useState(false);
  const {
    deleteModalVisible,
    flatPagination,
    handleConfirmDelete,
    handleDeleteClick,
    handlePageChange,
    handleRowClick,
    isFlatMode,
    isMobileTableScroll,
    isScrollMode,
    isWideScreen,
    scrollHostRef,
    loadingRowKey,
    setDeleteModalVisible,
    tableData,
    selectedRow,
    toggleGroup,
    toggleSubGroup,
  } = useInventoryTable({
    response,
    page,
    setPage,
    isLoading,
    setShowDetailDrawer,
    deepLinkedInventoryId,
    onDeleteSuccess,
    onLoadMoreGroups,
    onLoadMoreSubgroups,
    onLoadMoreGroupItems,
    onLoadMoreSubgroupItems,
  });

  const isGrouped = !isFlatMode;
  const isPerDeviceView = inventoryView === 'per_device';
  const selectedInventoryId = selectedRow?.inventoryId || deepLinkedInventoryId;
  const tableColumnCount = isPerDeviceView
    ? INVENTORY_DEVICE_TABLE_COLUMN_COUNT
    : INVENTORY_TABLE_COLUMN_COUNT;
  const tableScrollWidth = isPerDeviceView
    ? INVENTORY_DEVICE_MOBILE_SCROLL_WIDTH
    : INVENTORY_MOBILE_SCROLL_WIDTH;

  const columns = useInventoryColumns({
    handleDeleteClick,
    handleEditInventory,
    handleRowClick,
    isFlatMode,
    isMobileTableScroll,
    isPerDeviceView,
    isScrollMode,
    isWideScreen,
    loadingRowKey,
    onSortChange,
    sortDirection,
    sortKey,
    tableColumnCount,
    toggleGroup,
    toggleSubGroup,
  });

  const handleDownloadDevice = async () => {
    if (!selectedRow?.inventoryId) return;
    setDrawerMenuOpen(false);

    try {
      const response = await DownloadInventoryRecord(
        selectedRow.inventoryId,
        token
      );
      saveBlobAsFile(
        response,
        `${selectedRow.deviceId || 'inventory-record'}.xlsx`
      );
    } catch (error) {
      unauthorize(error);
      message.error('Failed to download inventory record');
    }
  };

  const deviceDrawerMenu = isPerDeviceView ? (
    <Dropdown
      open={drawerMenuOpen}
      onOpenChange={setDrawerMenuOpen}
      trigger={['click']}
      placement='bottomRight'
      dropdownRender={() => (
        <div className='inventory-device-drawer-menu'>
          <button type='button' onClick={handleDownloadDevice}>
            Download
          </button>
          <button
            type='button'
            onClick={() => {
              setDrawerMenuOpen(false);
              if (selectedRow) handleDeleteClick(selectedRow);
            }}
          >
            Delete
          </button>
        </div>
      )}
    >
      <span className='dots-icon-wrapper' aria-label='Device actions'>
        <DotsIcon color='var(--text-primary)' width={14} height={14} />
      </span>
    </Dropdown>
  ) : null;

  return (
    <>
      <div
        className={`settings-content-wrapper-inventory ${isGrouped ? 'is-grouped' : ''} ${
          isMobileTableScroll ? 'is-mobile-scroll' : ''
        }`}
      >
        <div ref={scrollHostRef}>
          <Table<FlatRow>
            columns={columns}
            dataSource={tableData}
            pagination={false}
            className='settings-table inventory-table'
            tableLayout='fixed'
            size='middle'
            loading={isLoading}
            scroll={isMobileTableScroll ? { x: tableScrollWidth } : undefined}
            rowClassName={(record) => {
              if (record.rowType === 'loader') return 'inventory-loader-row';
              if (record.rowType === 'load_more')
                return 'inventory-load-more-row';

              if (record.rowType === 'group') {
                return record.__depth === 1
                  ? 'inventory-table-subgroup-row'
                  : 'inventory-table-group-row';
              }

              return 'inventory-item-row';
            }}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
            })}
          />
        </div>
      </div>

      {isFlatMode && flatPagination && tableData.length > 0 && (
        <Row className='inventory-pagination-row' justify='center'>
          <Col>
            <CustomPagination
              page={page}
              totalPages={flatPagination.totalPages}
              onPageChange={handlePageChange}
            />
          </Col>
        </Row>
      )}

      <CommonDrawer
        width={444}
        open={showDetailDrawer}
        className={`inventory-detail-common-drawer ${
          isPerDeviceView ? 'inventory-device-detail-drawer' : ''
        } ${showDeviceHistory ? 'inventory-history-list-drawer' : ''}`}
        headerActions={showDeviceHistory ? null : deviceDrawerMenu}
        onBack={
          showDeviceHistory ? () => setShowDeviceHistory(false) : undefined
        }
        onClose={() => {
          setShowDeviceHistory(false);
          setShowDetailDrawer?.(false);
          onCloseDetailDrawer?.();
        }}
      >
        {selectedInventoryId && showDeviceHistory ? (
          <InventoryDeviceHistoryList inventoryId={selectedInventoryId} />
        ) : selectedInventoryId ? (
          <InventoryDetailDrawer
            id={selectedInventoryId}
            handleDeleteClick={handleDeleteClick}
            handleEditInventory={handleEditInventory}
            showDetailDrawer={showDetailDrawer}
            hideActions={isPerDeviceView}
            onOpenDeviceHistory={() => setShowDeviceHistory(true)}
            onDetailsLoaded={
              deepLinkedInventoryId === selectedInventoryId
                ? onDeepLinkedInventoryLoaded
                : undefined
            }
          />
        ) : null}
      </CommonDrawer>
      <CustomDeleteModal
        visible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onConfirm={handleConfirmDelete}
        title='Action needed'
        message='Are you sure you want to delete this record? This action cannot be undone.'
        cancelText='Cancel'
        confirmText='Delete'
        confirmColor='var(--danger)'
      />
    </>
  );
};

export default InventoryTable;
